import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "../api/schema";
import { getRuntimeConfig } from "../config";
import type { ApiError } from "./errors";
import { beginOidcRedirect } from "./auth";

// The single typed entrypoint for every customer `/v1` call
// (docs/09-frontend-surface.md §2). All SPA network access goes through this
// generated client — never an ad-hoc fetch/axios. `credentials: "include"`
// makes the HttpOnly session cookie ride along; the client attaches no bearer
// token and sends no customer `/v1` `organization_id` (tenant scope is
// server-resolved, §3).

// On any unauthenticated response (401 / AUTHENTICATION_REQUIRED) hand off to
// the OIDC/SAML redirect; the SPA never refreshes or mints tokens itself.
const authMiddleware: Middleware = {
  onResponse({ response }) {
    if (response.status === 401) {
      beginOidcRedirect();
    }
    return undefined;
  },
};

function responseToErrorCode(response: Response): ApiError["code"] {
  if (response.status === 401) {
    return "AUTHENTICATION_REQUIRED";
  }
  if (response.status === 403) {
    return "PERMISSION_DENIED";
  }
  if (response.status === 404) {
    return "NOT_FOUND";
  }
  if (response.status === 409) {
    return "CONFLICT";
  }
  if (response.status === 422 || response.status === 400) {
    return "VALIDATION_FAILED";
  }
  if (response.status === 429) {
    return "RATE_LIMITED";
  }
  if (response.status >= 500) {
    return "DEPENDENCY_UNAVAILABLE";
  }
  return "VALIDATION_FAILED";
}

function hasJsonContentType(candidate: string | null): boolean {
  return candidate?.includes("application/json") === true;
}

function isStatusEnvelope(candidate: unknown): candidate is ApiError {
  return (
    typeof candidate === "object" &&
    candidate !== null &&
    typeof (candidate as ApiError).code === "string" &&
    typeof (candidate as ApiError).message === "string" &&
    typeof (candidate as ApiError).request_id === "string" &&
    typeof (candidate as ApiError).retryable === "boolean" &&
    ((candidate as ApiError).retry_after === null ||
      typeof (candidate as ApiError).retry_after === "number")
  );
}

const missingBodyResponseErrorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok) {
      return undefined;
    }

    const bodyText = await response.clone().text();
    if (
      bodyText.trim().length > 0 &&
      hasJsonContentType(response.headers.get("content-type"))
    ) {
      let parsedBody: unknown;
      try {
        parsedBody = JSON.parse(bodyText);
      } catch {
        parsedBody = undefined;
      }
      if (isStatusEnvelope(parsedBody)) {
        return undefined;
      }
    }

    const headers = new Headers(response.headers);
    headers.set("content-type", "application/json");
    const statusError: ApiError = {
      code: responseToErrorCode(response),
      message: `Request failed with ${String(response.status)} ${response.statusText || "status"}`,
      request_id:
        response.headers.get("x-request-id") ??
        `http-${String(response.status)}`,
      retry_after: null,
      retryable: response.status >= 500,
    };

    return new Response(JSON.stringify(statusError), {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  },
};

export const apiClient = createClient<paths>({
  baseUrl: getRuntimeConfig().apiBaseUrl,
  credentials: "include",
});

apiClient.use(authMiddleware);
apiClient.use(missingBodyResponseErrorMiddleware);
